"use client";

import { Routes } from "@/api";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { ModalTrigger } from "@/components/ui/modal";
import { useIsDesktop } from "@/hooks/layout/use-is-desktop";
import { cn } from "@/lib/utils";

import { useToggle } from "@/hooks/use-toggle";
import { useIsSubscriptionActive } from "@/providers/subscription-provider";
import { Portfolio } from "@/services/portfolios/schema";
import {
  parseExchangeSource,
  parseWalletSource,
} from "@/utils/portfolios/parse-portfolio-source";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { PropsWithChildren } from "react";
import {
  PortfolioNavEditButton,
  PortfolioNavRemoveButton,
} from "../portfolio-nav";
import { PortfolioExchangeImage } from "./portfolio-exchange-image";
import { PortfolioWalletInfo } from "./portfolio-wallet-info";

interface PortfoliosLayoutProps {
  readonly portfolio: Portfolio;
  readonly exchangeInfo?: ReturnType<typeof parseExchangeSource>;
  readonly walletInfo?: ReturnType<typeof parseWalletSource>;
}

export function PortfolioListItem(
  props: PropsWithChildren<PortfoliosLayoutProps>,
) {
  const isSubscriptionActive = useIsSubscriptionActive();
  const isDesktop = useIsDesktop();
  const pathname = usePathname() ?? "";
  const portfolioId = pathname.split("/")[2];
  const openToggle = useToggle();

  const Wrapper = ({ children }: PropsWithChildren) =>
    isDesktop ? children : <ModalTrigger asChild>{children}</ModalTrigger>;

  const LinkWrapper = ({ children }: PropsWithChildren) =>
    isSubscriptionActive ? (
      <Link
        className={cn(
          "mb-1 flex flex-col rounded-lg p-2",
          props.portfolio.id === portfolioId ? "bg-accent" : "hover:bg-accent",
        )}
        href={Routes.PORTFOLIO(props.portfolio.id!)}
        prefetch={false}
      >
        {children}
      </Link>
    ) : (
      <div
        className={cn(
          "mb-1 flex flex-col rounded-lg p-2",
          props.portfolio.id === portfolioId ? "bg-accent" : "hover:bg-accent",
        )}
      >
        {children}
      </div>
    );

  return (
    <Wrapper>
      <LinkWrapper>
        <ContextMenu onOpenChange={openToggle.toggle}>
          <ContextMenuTrigger>
            <div className="flex items-center justify-between gap-1 font-semibold">
              {props.portfolio.name}
              {props.exchangeInfo ? (
                <PortfolioExchangeImage
                  src={props.exchangeInfo.image}
                  alt={props.exchangeInfo.name ?? "exchange"}
                />
              ) : null}
              {props.walletInfo?.length ? (
                <PortfolioWalletInfo
                  wallets={props.walletInfo}
                  variant="compact"
                />
              ) : null}
            </div>
            {/* {props.portfolio.createdAt && !isDevMode ? (
          <div className="text-sm">
            {formatDateToUTC(new Date(props.portfolio.createdAt))}
          </div>
        ) : null} */}
            <div className="[&_p]:text-sm [&_p]:font-normal [&_p]:text-accent-foreground">
              {props.children}
            </div>
          </ContextMenuTrigger>
          {openToggle.value ? (
            <ContextMenuContent className="flex flex-col">
              <ContextMenuItem asChild>
                <PortfolioNavEditButton
                  portfolioId={props.portfolio.id!}
                  onEdit={openToggle.setOff}
                />
              </ContextMenuItem>
              <ContextMenuItem asChild>
                <PortfolioNavRemoveButton
                  portfolioId={props.portfolio.id!}
                  portfolioName={props.portfolio.name}
                  onRemove={openToggle.setOff}
                />
              </ContextMenuItem>
            </ContextMenuContent>
          ) : null}
        </ContextMenu>
      </LinkWrapper>
    </Wrapper>
  );
}
